import React, { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../services/api';
import {
  AdminUser,
  MeResponse,
  Organisation,
  OrganisationRole,
} from '../../types';
import Navbar from '../shared/Navbar';

interface PickerOption {
  id: number;
  label: string;
  description?: string;
}

const AutocompletePicker: React.FC<{
  options: PickerOption[];
  placeholder: string;
  disabled: boolean;
  onSelect: (id: number) => void;
  onCancel: () => void;
}> = ({ options, placeholder, disabled, onSelect, onCancel }) => {
  const [query, setQuery] = useState('');
  const resultsId = React.useId();
  const filteredOptions = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase();
    const matches = normalized
      ? options.filter(option =>
          `${option.label} ${option.description ?? ''}`.toLocaleLowerCase().includes(normalized))
      : options;
    return matches.slice(0, 8);
  }, [options, query]);

  return (
    <div className="org-picker">
      <input
        type="search"
        value={query}
        onChange={event => setQuery(event.target.value)}
        placeholder={placeholder}
        autoFocus
        disabled={disabled}
        role="combobox"
        aria-autocomplete="list"
        aria-controls={resultsId}
        aria-expanded={filteredOptions.length > 0}
      />
      <button type="button" className="tm-button tm-button--secondary org-secondary-btn" onClick={onCancel}>Cancel</button>
      <div className="org-picker-results" role="listbox" id={resultsId}>
        {filteredOptions.length === 0 ? (
          <div className="org-picker-empty">No matches</div>
        ) : filteredOptions.map(option => (
          <button
            key={option.id}
            type="button"
            role="option"
            aria-selected="false"
            onClick={() => onSelect(option.id)}
            disabled={disabled}
          >
            <span>{option.label}</span>
            {option.description && <small>{option.description}</small>}
          </button>
        ))}
      </div>
    </div>
  );
};

const ManageOrganisationsPage: React.FC = () => {
  const [me, setMe] = useState<MeResponse | null>(null);
  const [organisations, setOrganisations] = useState<Organisation[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [detail, setDetail] = useState<Organisation | null>(null);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [newOrganisationName, setNewOrganisationName] = useState('');
  const [newOrganisationAllowsPublic, setNewOrganisationAllowsPublic] = useState(true);
  const [editingName, setEditingName] = useState('');
  const [memberPickerOpen, setMemberPickerOpen] = useState(false);
  const [newMemberRole, setNewMemberRole] = useState<OrganisationRole>('STUDENT');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadOrganisations = useCallback(async (preferredId?: number | null) => {
    const response = await api.getOrganisations();
    setOrganisations(response.organisations);
    setSelectedId(current => {
      const candidate = preferredId === undefined ? current : preferredId;
      if (candidate && response.organisations.some(item => item.id === candidate)) {
        return candidate;
      }
      return response.organisations[0]?.id ?? null;
    });
  }, []);

  const loadDetail = useCallback(async (organisationId: number) => {
    const response = await api.getOrganisation(organisationId);
    setDetail(response.organisation);
    setEditingName(response.organisation.name);
  }, []);

  useEffect(() => {
    const bootstrap = async () => {
      try {
        const current = await api.me();
        setMe(current);
        if (!(current.authenticated && current.user.is_admin)) return;
        const [organisationResponse, userResponse] = await Promise.all([
          api.getOrganisations(),
          api.getUsers(),
        ]);
        setOrganisations(organisationResponse.organisations);
        setSelectedId(organisationResponse.organisations[0]?.id ?? null);
        setUsers(userResponse.users);
      } catch (err: any) {
        setError(err?.message || 'Failed to load organisation management');
      } finally {
        setLoading(false);
      }
    };
    void bootstrap();
  }, []);

  useEffect(() => {
    setMemberPickerOpen(false);
    if (selectedId === null) {
      setDetail(null);
      return;
    }
    setError(null);
    void loadDetail(selectedId).catch((err: any) => {
      setError(err?.message || 'Failed to load organisation');
    });
  }, [loadDetail, selectedId]);

  const organisationUsers = useMemo(
    () => users.filter(user => user.organisation_id === selectedId),
    [selectedId, users],
  );
  const usersOutsideOrganisation = useMemo(
    () => users.filter(user => user.organisation_id !== selectedId),
    [selectedId, users],
  );

  const runMutation = async (operation: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await operation();
    } catch (err: any) {
      setError(err?.message || 'The update failed');
    } finally {
      setBusy(false);
    }
  };

  const handleCreateOrganisation = (event: FormEvent) => {
    event.preventDefault();
    const name = newOrganisationName.trim();
    if (!name) return;
    void runMutation(async () => {
      const response = await api.createOrganisation({
        name,
        allow_public_courses: newOrganisationAllowsPublic,
      });
      setNewOrganisationName('');
      setNewOrganisationAllowsPublic(true);
      await loadOrganisations(response.organisation.id);
    });
  };

  const handleSaveOrganisation = () => {
    if (!detail || !editingName.trim()) return;
    void runMutation(async () => {
      await api.updateOrganisation(detail.id, { name: editingName.trim() });
      await Promise.all([loadOrganisations(detail.id), loadDetail(detail.id)]);
    });
  };

  const handlePublicCourseToggle = (allowPublicCourses: boolean) => {
    if (!detail) return;
    void runMutation(async () => {
      const response = await api.updateOrganisation(detail.id, {
        allow_public_courses: allowPublicCourses,
      });
      setDetail(current => current
        ? { ...current, allow_public_courses: response.organisation.allow_public_courses }
        : current);
      await loadOrganisations(detail.id);
    });
  };

  const handleDeleteOrganisation = () => {
    if (!detail || !window.confirm(`Delete ${detail.name} and all of its classes? Users will be unassigned.`)) return;
    void runMutation(async () => {
      await api.deleteOrganisation(detail.id);
      setDetail(null);
      await loadOrganisations(null);
      const response = await api.getUsers();
      setUsers(response.users);
    });
  };

  const handleAddOrganisationUser = (userId: number) => {
    if (!detail) return;
    const user = users.find(item => item.id === userId);
    if (!user) return;
    if (
      user.organisation_name
      && !window.confirm(
        `${user.email} currently belongs to ${user.organisation_name}. Move them to ${detail.name}?`,
      )
    ) {
      return;
    }
    void runMutation(async () => {
      const response = await api.setUserOrganisation(userId, detail.id, newMemberRole);
      setUsers(current => current.map(item => item.id === userId ? response.user : item));
      setMemberPickerOpen(false);
      setNewMemberRole('STUDENT');
      await Promise.all([loadOrganisations(detail.id), loadDetail(detail.id)]);
    });
  };

  const handleRemoveOrganisationUser = (user: AdminUser) => {
    if (!detail || !window.confirm(
      `Remove ${user.email} from ${detail.name}? They will also be removed from all classes.`,
    )) return;
    void runMutation(async () => {
      const response = await api.setUserOrganisation(user.id, null, null);
      setUsers(current => current.map(item => item.id === user.id ? response.user : item));
      await Promise.all([loadOrganisations(detail.id), loadDetail(detail.id)]);
    });
  };

  const isAdmin = Boolean(me?.authenticated && me.user.is_admin);

  return (
    <div className="app">
      <Navbar />
      <main className="app-main">
        <div className="content-container organisation-admin">
          <div className="page-intro">
            <h2>Manage Organisations</h2>
            <p>Create organisations and classes, then assign users and course access.</p>
          </div>

          {!loading && !isAdmin && (
            <div className="admin-access-warning">
              <strong>Admin access required</strong>
              <span>You must be signed in as a global admin to manage organisations.</span>
              <div>
                <Link className="tm-button tm-button--primary ml-replace-btn" to="/login">Sign In</Link>
                <Link className="tm-button tm-button--secondary org-secondary-btn" to="/">Back to Home</Link>
              </div>
            </div>
          )}

          {isAdmin && (
            <>
              {error && <div className="org-error" role="alert">{error}</div>}
              <section className="tm-surface org-create-card">
                <h3>New organisation</h3>
                <form className="org-inline-form" onSubmit={handleCreateOrganisation}>
                  <input
                    value={newOrganisationName}
                    onChange={event => setNewOrganisationName(event.target.value)}
                    placeholder="Organisation name"
                    maxLength={255}
                    disabled={busy}
                  />
                  <label className="org-checkbox-label">
                    <input
                      type="checkbox"
                      checked={newOrganisationAllowsPublic}
                      onChange={event => setNewOrganisationAllowsPublic(event.target.checked)}
                      disabled={busy}
                    />
                    Allow public courses
                  </label>
                  <button className="tm-button tm-button--primary ml-replace-btn" type="submit" disabled={busy || !newOrganisationName.trim()}>
                    Create
                  </button>
                </form>
              </section>

              <div className="org-admin-layout">
                <aside className="tm-surface org-list-panel">
                  <h3>Organisations</h3>
                  {organisations.length === 0 ? (
                    <div className="ml-empty">No organisations yet.</div>
                  ) : organisations.map(organisation => (
                    <button
                      key={organisation.id}
                      type="button"
                      className={`org-list-item${selectedId === organisation.id ? ' active' : ''}`}
                      onClick={() => setSelectedId(organisation.id)}
                    >
                      <strong>{organisation.name}</strong>
                      <span>{organisation.user_count} users · {organisation.class_count} classes</span>
                    </button>
                  ))}
                </aside>

                <section className="tm-surface org-detail-panel">
                  {selectedId !== null && !detail ? (
                    <div className="ml-subtle">Loading organisation…</div>
                  ) : !detail ? (
                    <div className="ml-empty">Create an organisation to begin.</div>
                  ) : (
                    <>
                      <div className="org-detail-header">
                        <div className="org-name-editor">
                          <input
                            value={editingName}
                            onChange={event => setEditingName(event.target.value)}
                            maxLength={255}
                            disabled={busy}
                          />
                          <button
                            type="button"
                            className="tm-button tm-button--primary ml-replace-btn"
                            onClick={handleSaveOrganisation}
                            disabled={busy || !editingName.trim() || editingName.trim() === detail.name}
                          >
                            Save name
                          </button>
                        </div>
                        <div className="org-card-actions">
                          <Link className="tm-button tm-button--secondary org-secondary-btn" to={`/manage/${detail.id}`}>
                            Manage classes
                          </Link>
                          <button
                            type="button"
                            className="tm-button tm-button--danger ml-delete-btn"
                            onClick={handleDeleteOrganisation}
                            disabled={busy}
                          >
                            Delete organisation
                          </button>
                        </div>
                      </div>

                      <label className="org-policy-toggle">
                        <input
                          type="checkbox"
                          checked={detail.allow_public_courses}
                          onChange={event => handlePublicCourseToggle(event.target.checked)}
                          disabled={busy}
                        />
                        <span>
                          <strong>Allow public courses</strong>
                          <small>Members can browse public courses in addition to class assignments.</small>
                        </span>
                      </label>

                      <section className="org-members-section">
                        <div className="org-section-heading">
                          <div>
                            <h3>Organisation members</h3>
                            <span>{organisationUsers.length} users</span>
                          </div>
                          {!memberPickerOpen && (
                            <button
                              type="button"
                              className="tm-button tm-button--secondary org-secondary-btn"
                              onClick={() => setMemberPickerOpen(true)}
                              disabled={busy || usersOutsideOrganisation.length === 0}
                            >
                              Add user
                            </button>
                          )}
                        </div>
                        {organisationUsers.length === 0 ? (
                          <div className="ml-empty">No users belong to this organisation.</div>
                        ) : (
                          <div className="org-member-list">
                            {organisationUsers.map(user => (
                              <article className="org-member-card" key={user.id}>
                                <div>
                                  <strong>
                                    <Link className="managed-user-link" to={`/manage/user/${user.id}`}>
                                      {user.email}
                                    </Link>
                                  </strong>
                                  <small>{user.organisation_role?.toLowerCase()}</small>
                                </div>
                                <footer>
                                  <button
                                    type="button"
                                    className="org-remove-text-btn"
                                    onClick={() => handleRemoveOrganisationUser(user)}
                                    disabled={busy}
                                  >
                                    Remove
                                  </button>
                                </footer>
                              </article>
                            ))}
                          </div>
                        )}
                        {memberPickerOpen && (
                          <div className="org-member-picker">
                            <label>
                              Role
                              <select
                                value={newMemberRole}
                                onChange={event => setNewMemberRole(event.target.value as OrganisationRole)}
                                disabled={busy}
                              >
                                <option value="ADMIN">Admin</option>
                                <option value="TEACHER">Teacher</option>
                                <option value="STUDENT">Student</option>
                              </select>
                            </label>
                            <AutocompletePicker
                              options={usersOutsideOrganisation.map(user => ({
                                id: user.id,
                                label: user.email,
                                description: user.organisation_name
                                  ? `Currently in ${user.organisation_name}`
                                  : 'No organisation',
                              }))}
                              placeholder="Search users by email"
                              disabled={busy}
                              onSelect={handleAddOrganisationUser}
                              onCancel={() => {
                                setMemberPickerOpen(false);
                                setNewMemberRole('STUDENT');
                              }}
                            />
                          </div>
                        )}
                      </section>

                      <div className="org-management-callout">
                        <div>
                          <strong>{detail.class_count} classes</strong>
                          <span>Class membership and course access are managed on the organisation page.</span>
                        </div>
                        <Link className="tm-button tm-button--primary ml-replace-btn" to={`/manage/${detail.id}`}>
                          Manage classes
                        </Link>
                      </div>
                    </>
                  )}
                </section>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default ManageOrganisationsPage;
